import { useEffect, useRef } from "react";

interface UseDragResizeOptions {
  clamp: (value: number) => number;
  direction: 1 | -1;
  onResize: (width: number) => void;
  onResizeEnd?: () => void;
  onResizeStart?: () => void;
  width: number;
}

/**
 * Drag-to-resize driven by the Pointer Events API so mouse, touch, and stylus
 * all work with a single code path. The handle captures the pointer on down,
 * so `pointermove` / `pointerup` / `pointercancel` are delivered to the
 * element even when the pointer leaves the viewport.
 *
 * End listeners (`pointerup`, `pointercancel`, `lostpointercapture`) are
 * registered before `onResizeStart` so a very fast click/drag cannot miss
 * them. If `setPointerCapture` fails (pointer already released), the drag
 * ends immediately so consumers are not left stuck in a resizing state.
 *
 * Width is derived from absolute pointer position against a fixed origin
 * captured at pointerdown (`clientX - direction * width`). That preserves
 * grab offset and avoids clamp dead-zones: once min/max is hit, further
 * travel past the edge is ignored until the pointer re-crosses it.
 *
 * Consumers should set `touch-action: none` on the target (the FlexHandle
 * applies `touch-none`) to prevent the browser from claiming touch gestures
 * for scrolling during a drag.
 */
export function useDragResize({
  width,
  direction,
  clamp,
  onResize,
  onResizeStart,
  onResizeEnd,
}: UseDragResizeOptions) {
  const dragControllerRef = useRef<AbortController | null>(null);
  const endActiveDragRef = useRef<(() => void) | null>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const rafIdRef = useRef(0);
  const activePointerIdRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      endActiveDragRef.current?.();
      cancelAnimationFrame(rafIdRef.current);
    },
    [],
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    endActiveDragRef.current?.();

    const controller = new AbortController();
    dragControllerRef.current = controller;
    const target = e.currentTarget;
    const pointerId = e.pointerId;
    activePointerIdRef.current = pointerId;

    const originX = e.clientX - direction * width;
    let latestX = e.clientX;
    let hasEnded = false;

    const flushAndEnd = () => {
      if (hasEnded) return;
      hasEnded = true;

      if (rafIdRef.current !== 0) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
        onResize(clamp(direction * (latestX - originX)));
      }

      controller.abort();
      dragControllerRef.current = null;
      endActiveDragRef.current = null;
      activePointerIdRef.current = null;

      try {
        if (target.hasPointerCapture(pointerId)) {
          target.releasePointerCapture(pointerId);
        }
      } catch {
        // Already released or target unmounted — ignore.
      }

      target.blur();
      onResizeEnd?.();
    };

    endActiveDragRef.current = flushAndEnd;
    const { signal } = controller;

    target.addEventListener(
      "pointermove",
      (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        latestX = ev.clientX;
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = 0;
          onResize(clamp(direction * (latestX - originX)));
        });
      },
      { signal },
    );

    const endEvents = [
      "pointerup",
      "pointercancel",
      "lostpointercapture",
    ] as const;
    for (const type of endEvents) {
      target.addEventListener(
        type,
        (ev: Event) => {
          if (!(ev instanceof PointerEvent) || ev.pointerId !== pointerId) {
            return;
          }
          flushAndEnd();
        },
        { signal },
      );
    }

    let hasCapture = false;
    try {
      target.setPointerCapture(pointerId);
      hasCapture = target.hasPointerCapture(pointerId);
    } catch {
      hasCapture = false;
    }

    onResizeStart?.();

    if (!hasCapture) {
      flushAndEnd();
    }
  };

  return { handleRef, handlePointerDown };
}
